import { Download, FileText, ExternalLink } from "lucide-react";
import Seo from "@/components/Seo";

const RESULTS = [
  {
    label: "Dark Growers Clones",
    sublabel: "Sample ID 260707R031 · DG-HEMP-CLONE",
    pdf: "/coa-clones.pdf",
    thumb: "/coa-clones.png",
  },
  {
    label: "Dark Growers Seeds",
    sublabel: "Sample ID 260707R032 · DG-SEEDS",
    pdf: "/coa-seeds.pdf",
    thumb: "/coa-seeds.png",
  },
];

function COACard({ r, index }) {
  return (
    <article data-testid={`coa-card-${index}`} className="border border-white/10 bg-[#0d0d0d]">
      <div className="px-6 py-5 border-b border-white/10">
        <h2 className="font-display text-2xl md:text-3xl text-slate-100 flex items-center gap-3">
          <FileText size={20} className="text-emerald-500 shrink-0" /> {r.label}
        </h2>
        <p className="text-slate-500 text-xs mt-1.5">{r.sublabel}</p>
      </div>

      {/* Desktop: embedded PDF viewer */}
      <div className="hidden md:block bg-black">
        <object data={`${r.pdf}#view=FitH`} type="application/pdf" className="w-full h-[720px]" data-testid={`coa-embed-${index}`}>
          <a href={r.pdf} target="_blank" rel="noopener noreferrer" className="block">
            <img src={r.thumb} alt={`${r.label} Certificate of Analysis`} loading="lazy" decoding="async" className="w-full" />
          </a>
        </object>
      </div>

      {/* Mobile: clickable thumbnail that opens the full PDF */}
      <a
        href={r.pdf}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`coa-thumb-${index}`}
        className="md:hidden block relative group"
      >
        <img src={r.thumb} alt={`${r.label} Certificate of Analysis — tap to open`} loading="lazy" decoding="async" className="w-full" />
        <span className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-active:opacity-100 transition-opacity">
          <span className="inline-flex items-center gap-2 bg-emerald-500 text-[#050505] px-4 py-2 text-sm font-medium">
            Open Full COA <ExternalLink size={14} />
          </span>
        </span>
      </a>

      <div className="px-6 py-5 border-t border-white/10 flex flex-wrap gap-3">
        <a
          href={r.pdf}
          download
          data-testid={`coa-download-${index}`}
          className="inline-flex items-center gap-2 bg-emerald-600 text-white hover:bg-emerald-500 transition-colors px-6 py-3 text-sm font-medium"
        >
          <Download size={15} /> Download PDF
        </a>
        <a
          href={r.pdf}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 border border-white/20 text-slate-300 hover:text-white hover:border-white/40 transition-colors px-6 py-3 text-sm"
        >
          Open in new tab <ExternalLink size={14} />
        </a>
      </div>
    </article>
  );
}

export default function COA() {
  return (
    <div className="bg-[#050505]">
      <Seo
        title="Certificate of Analysis (COA) — SC Labs Lab Results | Dark Growers Genetics"
        description="View Dark Growers Genetics Certificates of Analysis. All our genetics are lab tested by SC Labs — download the full COAs for our clones and seeds."
        canonical="https://darkgrowers.com/coa"
      />
      <section className="max-w-4xl mx-auto px-6 lg:px-8 py-16">
        <p className="label-caps text-emerald-500 mb-3">Lab Tested by SC Labs</p>
        <h1 className="font-display text-5xl md:text-6xl text-slate-100">Certificate of Analysis</h1>
        <p className="text-slate-400 text-sm mt-5 max-w-2xl leading-relaxed" data-testid="coa-intro">
          All Dark Growers genetics are lab tested by SC Labs. View our full Certificates of Analysis below.
        </p>

        <div className="grid gap-10 mt-12">
          {RESULTS.map((r, i) => (
            <COACard key={i} r={r} index={i} />
          ))}
        </div>
      </section>
    </div>
  );
}
